import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * ProfileRankWidget
 *
 * Shows the current user's rank in their firm, the metric they're
 * ranked on, and their raw count. Uses the same
 * /api/dashboard/leaderboard endpoint the Dashboard's LeaderboardWidget
 * uses — but where the dashboard widget shows the top 5, this one
 * focuses on `me`: the caller's own row.
 *
 * Layout is intentionally card-shaped so it drops cleanly into a
 * grid slot in UserProfile.jsx alongside other profile-level cards.
 */
export default function ProfileRankWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/dashboard/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error('[banyak-profile-rank] fetch failed', res.status);
          setError('Failed to load');
          return;
        }
        setData(await res.json());
      } catch (e) {
        console.error('[banyak-profile-rank] fetch threw', e);
        setError('Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="text-xs text-gray-400 py-4 text-center">Loading rank…</div>
      </div>
    );
  }

  if (error || !data?.me) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="text-xs text-gray-400 py-4 text-center">
          {error || 'No ranking data yet'}
        </div>
      </div>
    );
  }

  const { me, metric, period } = data;
  const metricLabel = metric === 'messages_sent' ? 'Messages Sent' : metric;
  const periodLabel = period === 'quarter' ? 'This Quarter'
                    : period === 'month'   ? 'This Month' : period;
  const isTop3 = me.rank <= 3;
  const rankColor = me.rank === 1 ? 'text-amber-500'
                  : me.rank === 2 ? 'text-gray-400'
                  : me.rank === 3 ? 'text-amber-700'
                  :                 'text-gray-500';

  // Ordinal suffix for the rank number (1st / 2nd / 3rd / 4th…)
  const ordinal = (n) => {
    const s = ['th','st','nd','rd'];
    const v = n % 100;
    return n + (s[(v-20)%10] || s[v] || s[0]);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Your Rank
        </h3>
        <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">
          {metricLabel} · {periodLabel}
        </span>
      </div>

      <div className="flex items-center gap-4">
        {/* Rank number, big */}
        <div className={`text-4xl font-bold tabular-nums ${rankColor}`}>
          {ordinal(me.rank)}
        </div>

        <div className="h-12 w-px bg-gray-200" />

        {/* Count + firm size */}
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-bold text-gray-900 tabular-nums">
            {(me.messages_sent || 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            out of {me.firm_size?.toLocaleString?.() || '—'} teammate{me.firm_size === 1 ? '' : 's'}
          </div>
        </div>

        {isTop3 && (
          <div className="flex-shrink-0">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
              me.rank === 1 ? 'bg-amber-100 text-amber-800'
                            : me.rank === 2 ? 'bg-gray-100 text-gray-600'
                                            : 'bg-orange-100 text-orange-800'
            }`}>
              {me.rank === 1 ? '🏆 Top' : me.rank === 2 ? '🥈 Second' : '🥉 Third'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
